import * as THREE from 'three';
import { makePathFinderLineComponent, pathFinderLineComponentType } from '@/_models/virtualStaging/PathFinderLine/component/PathFinderLine.component';
import { ITargetObjectData } from '@/_models/virtualStaging/PathFinderLine/types';
import { getHorizontalRotationBetweenPoints, getVerticalRotationBetweenPoints } from '@/utils/three/getHorizontalRotationBetweenPoints';
import { timeout } from '@/utils/timeout';
import { getPath } from '@/api/queries';
import { transformModifiedNavigationSweeps } from '@/utils/data/sweeps';

export class ScenePathFinderLine {
  private showCaseTemp;

  private sweepGraph;

  private sweeps = [];

  private mpSweeps = [];

  private camera;

  private currentSweep;

  public setActionPathNavbarVisible;

  private pathArrowRef;

  public moveToSweep;

  public setCameraRotation;

  private component;

  private currentSID;

  private targetSID;

  public targetObjectData: any;

  public destinationTitle = 'Move to location';

  public inAutoNavigate: boolean;

  public onLoaded: any;

  public setActionPathFinished;

  public isLegacy = true; // If user didn't use navigationTool to upload sweeps to DB, set to true

  public tourId: number;

  public teleportGroups: any;

  public teleportPoints: any;

  public navigationType: string;

  public modelNode: any;

  public hasTeleports: boolean;

  public showTeleportLoading: any;

  public setActionPathNavbarPlayButtonType: any;

  constructor(
    showCaseTemp,
    camera,
    mpSweeps,
    modifiedSweeps,
    currentSweep,
    setActionPathNavbarVisible,
    pathArrowRef,
    moveToSweep,
    setCameraRotation,
    setActionPathFinished,
    tourId,
    teleportGroups,
    teleportPoints,
    showTeleportLoading,
    setActionPathNavbarPlayButtonType,
  ) {
    this.showCaseTemp = showCaseTemp;
    this.camera = camera;
    this.currentSweep = currentSweep;
    this.setActionPathNavbarVisible = setActionPathNavbarVisible;
    this.pathArrowRef = pathArrowRef;
    this.moveToSweep = moveToSweep;
    this.setCameraRotation = setCameraRotation;
    this.setActionPathFinished = setActionPathFinished;
    if (modifiedSweeps.find((s) => !s.isCustom)) {
      this.sweeps = transformModifiedNavigationSweeps(modifiedSweeps);
      this.isLegacy = false;
    }
    this.mpSweeps = mpSweeps;
    this.tourId = tourId;
    this.teleportGroups = teleportGroups;
    this.teleportPoints = teleportPoints;
    this.navigationType = 'shortest';
    this.hasTeleports = false;
    this.showTeleportLoading = showTeleportLoading;
    this.setActionPathNavbarPlayButtonType = setActionPathNavbarPlayButtonType;
    this.create();
  }

  async create() {
    this.showCaseTemp.sweep.subscribe((sweeps) => {
      sweeps.forEach((sweep) => {
        sweep.uuid = sweep.sid;
      });
      if (sweeps.length && !this.sweeps.length) this.sweeps = sweeps;
    });

    this.sweepGraph = await this.showCaseTemp.mpSDK.Sweep.createGraph();
    for (const { src, dst, weight } of this.sweepGraph.edges) {
      this.sweepGraph.setEdge({ src, dst, weight: weight ** 1.5 });
    }

    await this.showCaseTemp.showcase.Scene.register(pathFinderLineComponentType, makePathFinderLineComponent);

    const modelNode = await this.showCaseTemp.mpSDK.Scene.createNode();

    this.component = await modelNode.addComponent(pathFinderLineComponentType, {
      camera: this.camera,
      arrowRef: this.pathArrowRef,
      teleportPoints: this.teleportPoints,
    });
    await modelNode.start();
    if (this.onLoaded) this.onLoaded();

    this.initSweepUpdates();
  }

  initSweepUpdates() {
    this.showCaseTemp.showcase.Sweep.current.subscribe(async (currentSweep) => {
      if (currentSweep.sid || currentSweep.sweep) {
        this.currentSID = currentSweep.sid || currentSweep.sweep;
        if (!this.targetSID) return;
        if (this.currentSID === this.targetSID) {
          this.reset();
          return;
        }
        this.update(this.currentSID, this.targetSID, null, null);
      }
    });
  }

  getAngleBetweenPoints(point1, point2, point3) {
    const yawDegrees12 = getHorizontalRotationBetweenPoints(point1, point2);
    const yawDegrees23 = getHorizontalRotationBetweenPoints(point2, point3);

    return Math.abs(yawDegrees12 - yawDegrees23);
  }

  async rotateCameraToPathDirection() {
    const { path } = this.component.inputs;

    const currentPoint = path[0];
    const nextPoint = path[1];

    const pos1 = new THREE.Vector3(currentPoint.data.position.x, currentPoint.data.position.y, currentPoint.data.position.z);
    const pos2 = new THREE.Vector3(nextPoint.data.position.x, nextPoint.data.position.y, nextPoint.data.position.z);

    const yawDegrees = getHorizontalRotationBetweenPoints(pos1, pos2);
    const pitchDegrees = getVerticalRotationBetweenPoints(pos1, pos2) * 0.75;
    await this.setCameraRotation({ x: pitchDegrees, y: yawDegrees });
  }

  autoNavigateCurrentPath(setStep?: any, onComplete?: any, onCancel?: any, isAutoMoveEnabled?: boolean) {
    this.inAutoNavigate = true;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const { path } = this.component.inputs;

      const navigatePathStepsRecursive = async (currentPoint) => {
        const allTeleportPoints = this.teleportGroups.map((group) => group.points).flat();
        const currentIndex = path.indexOf(currentPoint);
        const nextPoint = path[currentIndex + 1];
        const sweepSet = new Set(allTeleportPoints.map((teleportPoint) => teleportPoint.sweep));
        const nexpointIsTp = sweepSet.has(nextPoint?.data?.id);
        const currentPointIsTp = sweepSet.has(currentPoint?.data?.id);

        if (!this.inAutoNavigate || this.currentSweep?.uuid === '') {
          resolve(false);
          return;
        }
        if ((currentPoint === path[path.length - 1] || currentPoint === path[path.length - 2]) && !currentPointIsTp && !nexpointIsTp) {
          return;
        }

        if (nexpointIsTp && currentPointIsTp) {
          this.inAutoNavigate = false;
          this.teleportPoints.forEach((tp) => {
            tp.pointNodes.forEach((pointNode) => {
              if (nextPoint.data.id === pointNode.sweep) {
                // Hide the teleport button on the end of teleport
                pointNode[0].obj3D.visible = false;
                pointNode.keepHidden = true;

                const foundInitialPointNode = tp.pointNodes.find((pn) => pn.sweep === currentPoint.data.id);
                const pos1 = new THREE.Vector3(currentPoint.data.position.x, currentPoint.data.position.y, currentPoint.data.position.z);
                const pos2 = new THREE.Vector3(foundInitialPointNode[0].position.x, foundInitialPointNode[0].position.y, foundInitialPointNode[0].position.z);
                const yawDegrees = getHorizontalRotationBetweenPoints(pos1, pos2);
                const pitchDegrees = getVerticalRotationBetweenPoints(pos1, pos2) * 0.75;

                this.setCameraRotation({ x: pitchDegrees, y: yawDegrees }).then(async () => {
                  if (isAutoMoveEnabled) {
                    setTimeout(() => {
                      this.showTeleportLoading(pointNode.name);
                      this.moveToSweep(nextPoint.data.sweepId, { x: 0, y: 0 }, 'INSTANT');
                    }, 2000);

                    setTimeout(() => {
                      if (path[path.length - 1]?.data?.id !== nextPoint?.data?.id) {
                        this.rotateCameraToPathDirection();
                      }
                      this.setActionPathNavbarPlayButtonType('Pause');
                      this.autoNavigateCurrentPath(setStep, onComplete, onCancel, isAutoMoveEnabled);
                    }, 5000);
                  }
                });
              }
            });
          });
          return;
        }

        // Uncomment to skip some sweeps during navigation
        // const nextPointPlusOne = path[currentIndex + 2] === path[path.length - 1] ? path[path.length - 2] : path[currentIndex + 2];

        const pos1 = new THREE.Vector3(currentPoint.data.position.x, currentPoint.data.position.y, currentPoint.data.position.z);
        const pos2 = new THREE.Vector3(nextPoint.data.position.x, nextPoint.data.position.y - 1.15, nextPoint.data.position.z);
        // const pos3 = new THREE.Vector3(nextPointPlusOne.data.position.x, nextPointPlusOne.data.position.y - 1.15, nextPointPlusOne.data.position.z);

        // const angle = this.getAngleBetweenPoints(pos1, pos2, pos3);

        // let targetPoint = angle > 45 ? nextPoint : nextPointPlusOne;
        // let targetPos = angle > 45 ? pos2 : pos3;
        //
        // if (nextPoint.data.isCustom !== nextPointPlusOne.data.isCustom) {
        //   targetPoint = nextPointPlusOne.data.isCustom ? nextPoint : nextPointPlusOne;
        //   targetPos = nextPointPlusOne.data.isCustom ? pos2 : pos3;
        // } else if (nextPoint.data.isCustom && nextPointPlusOne.data.isCustom) {
        //   targetPoint = nextPoint; // Prefer nextPoint if both are custom
        //   targetPos = pos2;
        // }

        const targetPoint = nextPoint;
        const targetPos = pos2;

        const yawDegrees = getHorizontalRotationBetweenPoints(pos1, targetPos);
        const pitchDegrees = getVerticalRotationBetweenPoints(pos1, targetPos) * 0.75;

        if (!targetPoint.data.isCustom) {
          // eslint-disable-next-line no-await-in-loop
          await this.setCameraRotation({ x: pitchDegrees, y: yawDegrees });

          const targetSID = this.sweeps.find((s) => s.id === targetPoint.data.id)?.sweepId || targetPoint.data.id;

          // eslint-disable-next-line no-await-in-loop
          await this.moveToSweep(targetSID, { x: pitchDegrees, y: yawDegrees }, 'FLY');
        }

        // sleep function to prevent tour crash when trying to move during automatic movement
        // eslint-disable-next-line no-await-in-loop
        await timeout(100);

        if (setStep) {
          setStep((prevState) => {
            const nextStep = prevState + parseFloat((100 / path.length).toFixed(0));
            return nextStep;
          });
        }

        await navigatePathStepsRecursive(targetPoint);
      };

      try {
        await navigatePathStepsRecursive(path[0]);
        resolve(true);
      } catch (error) {
        console.error('Error in autoNavigate:', error);
        reject(error);
      }
    }).then((completed) => {
      if (this.inAutoNavigate && completed) {
        if (onComplete) onComplete();
        this.showCaseTemp.showcase.Mattertag.navigateToTag(this.targetObjectData.sid, this.showCaseTemp.showcase.Mattertag.Transition.FLY);
        this.reset();
        this.setActionPathFinished(true);
        if (setStep) setStep(0);
      } else if (onCancel) onCancel();
    });
  }

  async update(currentSID, targetSID, title?: string, targetObjectData?: ITargetObjectData, navigationType?: string) {
    if (title) this.destinationTitle = title;
    if (targetObjectData) this.targetObjectData = targetObjectData;
    if (navigationType) this.navigationType = navigationType;

    if (this.navigationType !== 'noElevators') {
      this.teleportPoints.forEach((tp) => {
        tp.pointNodes.forEach((pointNode) => {
          if (!pointNode.keepHidden) pointNode[0].obj3D.visible = true;
        });
      });
    } else {
      this.teleportPoints.forEach((tp) => {
        tp.pointNodes.forEach((pointNode) => {
          pointNode[0].obj3D.visible = false;
        });
      });
    }

    if (this.isLegacy) {
      this.currentSID = currentSID;
      this.targetSID = targetSID;
    } else {
      this.currentSID = this.sweeps.find((s) => s.sweepId === currentSID)?.id?.toString() || currentSID;
      this.targetSID = this.sweeps.find((s) => s.sweepId === targetSID)?.id?.toString() || targetSID;
    }

    if (!this.currentSID || !this.targetSID) return;

    // If sweep_uuid is used instead of id, get sweepId from mpSweeps
    if (!this.isLegacy && !this.sweeps.find((sweep) => sweep.id === this.currentSID)) {
      const { id } = this.mpSweeps.find((mpSweep) => mpSweep.sweep_uuid === this.currentSID);
      this.currentSID = this.sweeps.find((sweep) => sweep.sweepId === id).id.toString();
    }

    if (!this.isLegacy && !this.sweeps.find((sweep) => sweep.id === this.targetSID)) {
      const { id } = this.mpSweeps.find((mpSweep) => mpSweep.sweep_uuid === this.targetSID);
      this.targetSID = this.sweeps.find((sweep) => sweep.sweepId === id).id.toString();
    }

    const res = await getPath(this.sweeps, this.currentSID, this.targetSID, this.tourId, this.navigationType);

    this.hasTeleports = res.hasTeleports;

    const objectLookup = {};
    this.sweeps.forEach((obj) => {
      objectLookup[obj.id] = obj;
    });
    const orderedObjects = res.path.map((id) => ({ data: objectLookup[id] })).filter((obj) => obj !== undefined);
    const isTargetSidTP = !!this.teleportGroups
      .map((group) => group.points)
      .flat()
      .find((point) => point.sweep === this.targetSID);

    if (this.currentSID === this.targetSID && !isTargetSidTP) {
      this.reset();
      return;
    }

    this.component.inputs.path = orderedObjects;

    const visible = this.targetObjectData?.enableNavBar || this.targetObjectData?.enableTeleportBtn || this.targetObjectData?.autoNavigation;
    this.setActionPathNavbarVisible(visible);
    // AStarRunner.dispose();
  }

  getAutonavigationPath() {
    const { path } = this.component.inputs;

    return path;
  }

  async reset() {
    this.currentSID = null;
    this.targetSID = null;
    this.component.inputs.path = [];
    this.destinationTitle = 'Move to location';
    this.setActionPathNavbarVisible(false);
    this.targetObjectData = null;
    this.inAutoNavigate = false;
    this.navigationType = 'shortest';
    this.hasTeleports = false;
  }
}
